import type { SavedSession } from '@slot-matrix/shared-types';

const DB_NAME = 'slot-matrix';
const DB_VERSION = 1;
const STORE_NAME = 'sessions';

/**
 * Open the IndexedDB database.
 */
async function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        db.createObjectStore(STORE_NAME, { keyPath: 'id' });
      }
    };

    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Save a session to IndexedDB.
 */
export async function saveSession(id: string, data: SavedSession): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.put({ ...data, id });
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * Load a session from IndexedDB.
 */
export async function loadSession(id: string): Promise<SavedSession | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.get(id);
    request.onsuccess = () => resolve(request.result ?? null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Delete a session from IndexedDB.
 */
export async function deleteSession(id: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readwrite');
    const store = tx.objectStore(STORE_NAME);
    const request = store.delete(id);
    request.onsuccess = () => resolve();
    request.onerror = () => reject(request.error);
  });
}

/**
 * List all saved sessions (id, name, updatedAt only).
 */
export async function listSessions(): Promise<{ id: string; name: string; updatedAt: string }[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(STORE_NAME, 'readonly');
    const store = tx.objectStore(STORE_NAME);
    const request = store.getAll();
    request.onsuccess = () => {
      const sessions = (request.result as SavedSession[]).map((s) => ({
        id: s.id,
        name: s.name,
        updatedAt: s.updatedAt,
      }));
      resolve(sessions);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Save theme preference to localStorage.
 */
export function saveTheme(theme: 'light' | 'dark'): void {
  localStorage.setItem('slot-matrix-theme', theme);
}

/**
 * Load theme preference from localStorage.
 */
export function loadTheme(): 'light' | 'dark' {
  const stored = localStorage.getItem('slot-matrix-theme');
  if (stored === 'dark' || stored === 'light') return stored;
  // Default to system preference
  if (window.matchMedia?.('(prefers-color-scheme: dark)').matches) return 'dark';
  return 'light';
}
